import { httpClient } from 'src/libs';

type IBoard = {
  page: number;
  per_page: number;
};

export const getAllBoards = async ({ page, per_page }: IBoard) => {
  try {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/boards?page=${page}&per_page=${per_page}`;
    const response = await httpClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching boards:', error);
    return error;
  }
};

export const getBoardDetailsById = async (boardId: string) => {
  try {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/boards/${boardId}`;
    const response = await httpClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching board details:', error);
    return error;
  }
};

export const createBoard = async (data: {
  title: string;
  subtitle?: string;
  description?: string;
  tags?: string[];
}) => {
  const response = await httpClient.post(
    `${process.env.REACT_APP_BASE_URL}/comms-api/boards`,
    data,
    {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );
  return response.data;
};

export const updateBoard = async (boardId: string, data: any) => {
  try {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/boards/${boardId}`;
    const response = await httpClient.patch(url, data);
    return response.data;
  } catch (error) {
    return error;
  }
};

export const deleteBoard = async (boardId: string) => {
  const response = await httpClient.delete(
    `${process.env.REACT_APP_BASE_URL}/comms-api/boards/${boardId}`,
    {
      headers: {
        Accept: 'application/json',
      },
    }
  );
  return response.data;
};

export const getAllBoardLists = async (boardId: string) => {
  try {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/lists?listable_id=${boardId}`;
    const response = await httpClient.get(url);
    return response.data;
  } catch (error) {
    return error;
  }
};

export const getList = async (listId: string) => {
  try {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/lists/${listId}`;
    const response = await httpClient.get(url);
    return response.data;
  } catch (error) {
    return error;
  }
};

export const createList = async (data: {
  listable_type: string;
  listable_id: string;
  title: string;
  subtitle?: string;
  description?: string;
  tags?: string[];
  order?: number;
}) => {
  try {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/lists`;
    const response = await httpClient.post(url, data);
    return response.data;
  } catch (error) {
    return error;
  }
};

export const updateList = async (listId: string, data: any) => {
  try {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/lists/${listId}`;
    const response = await httpClient.patch(url, data);
    return response.data;
  } catch (error) {
    return error;
  }
};

export const deleteList = async (listId: string) => {
  try {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/lists/${listId}`;
    const response = await httpClient.delete(url);
    return response.data;
  } catch (error) {
    return error;
  }
};

export const updatePositionList = async (
  listId: string,
  order: number
) => {
  try {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/lists/${listId}`;
    const response = await httpClient.patch(url, {
      order: order,
    });
    return response.data;
  } catch (error) {
    return error;
  }
};

export const getAllBoardListItems = async (listId: string, fullPayload: boolean = false) => {
  try {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/lists/${listId}/list_items${fullPayload ? '?full_payload=true' : ''}`;
    const response = await httpClient.get(url);
    return response.data;
  } catch (error) {
    return error;
  }
};

export const getListItem = async (listId: string, itemId: string) => {
  try {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/lists/${listId}/list_items/${itemId}`;
    const response = await httpClient.get(url);
    return response.data;
  } catch (error) {
    return error;
  }
};

export const createListItem = async (
  listId: string,
  data: {
    title: string;
    subtitle?: string;
    description?: string;
    tags?: string[];
    order?: number;
  }
) => {
  try {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/lists/${listId}/list_items`;
    const response = await httpClient.post(url, data);
    return response.data;
  } catch (error) {
    console.error('Error creating list item:', error);
    throw error;
  }
};

export const updateListItem = async (
  listId: string,
  itemId: string,
  data: any
) => {
  try {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/lists/${listId}/list_items/${itemId}`;

    const response = await httpClient.patch(url, data);
    return response.data;
  } catch (error) {
    console.error('Error updating list item:', error);
    throw error;
  }
};

export const deleteListItem = async (listId: string, itemId: string) => {
  try {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/lists/${listId}/list_items/${itemId}`;
    const response = await httpClient.delete(url);
    return response.data;
  } catch (error) {
    return error;
  }
};

export const updatePositionListItem = async (
  listId: string,
  itemId: string,
  newIndex: number
) => {
  try {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/lists/${listId}/list_items/${itemId}`;
    const response = await httpClient.patch(url, {
      order: newIndex,
    });

    return response.data;
  } catch (error) {
    return error;
  }
};

export const moveListItemToAnotherList = async (
  currentListId: string,
  targetListId: string,
  cardId: string
) => {
  try {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/lists/${currentListId}/list_items/${cardId}`;
    const response = await httpClient.patch(url, {
      list_id: targetListId,
    });
    return response.data;
  } catch (error) {
    console.error('Error moving item to another list:', error);
    return error;
  }
};
